// Organization modules (docs/features/MODULES.md), mirroring backend
// utils/orgModules.js. The logged-in user's organizationId comes back
// populated (name, orgType, enabledModules, modules) from /api/auth/login.
//   production  — leases, lease operating
//   pumper      — My Route / Route Setup, Well Detail → Tanks
//   rigServices — rig reports (tour sheets), jobs, customers, rigs-today panel
//   hydrotest   — hydrotest/scan tickets, price books (DIVISION-PL.md)

export function orgOf(user) {
  const org = user && user.organizationId;
  return org && typeof org === "object" ? org : {};
}

export const PRESETS = {
  operator: { production: true, pumper: true, rigServices: false, hydrotest: false },
  rig_service: { production: false, pumper: false, rigServices: true, hydrotest: false },
  operator_rig_service: { production: true, pumper: true, rigServices: true, hydrotest: false },
};

const KEYS = ["production", "pumper", "rigServices", "hydrotest"];

// An org without `modules` keeps what its old type gave it.
export function resolveModules(org) {
  const m = org && org.modules;
  if (m && KEYS.some((k) => typeof m[k] === "boolean")) {
    return Object.fromEntries(KEYS.map((k) => [k, m[k] === true]));
  }
  if (org && org.orgType === "service_company") {
    return { production: false, pumper: (org.enabledModules || []).includes("pumper"), rigServices: true, hydrotest: false };
  }
  return { ...PRESETS.operator };
}

export function orgModules(user) {
  return resolveModules(orgOf(user));
}

export function hasModule(user, mod) {
  return !!orgModules(user)[mod];
}

// Rig-service screens (tour sheets, jobs, customers, rigs-today panel).
export function isServiceCompany(user) {
  return hasModule(user, "rigServices");
}

// Production + rig services in one org.
export function hasBothModules(user) {
  const m = orgModules(user);
  return m.production && m.rigServices;
}

export function presetOf(modules) {
  const m = modules || {};
  const match = Object.entries(PRESETS).find(([, p]) => KEYS.every((k) => !!p[k] === !!m[k]));
  return match ? match[0] : "custom";
}

const PUMPER_TABS = ["PUMPER_ROUTE", "PUMPER_ROUTE_ADMIN"];
const PRODUCTION_TABS = ["LEASE", "LEASE_OPERATING"];
export const RIG_SERVICE_TABS = ["RIG_REPORTS", "RIGS", "JOBS", "CUSTOMERS"];
export const HYDRO_TABS = ["HYDRO_TICKETS"];

// A service company with no production side (rig services and / or
// hydro-testing): its menu is trimmed to what it uses (WORKFLOW-AUDIT.md
// stage 2) unless the admin turned on "Show all menu items".
export function isServiceOnly(user) {
  const m = orgModules(user);
  return !m.production && (m.rigServices || m.hydrotest);
}
export function simpleMenu(user) {
  return isServiceOnly(user) && !orgOf(user).menuShowAll;
}
// Screens a rig-service-only company has no use for (still reachable with
// "Show all menu items"; LEGAL moves to the account menu).
export const SIMPLE_MENU_HIDDEN = ["ANALYZE", "BHA_PERFORMANCE", "ANALYTICS", "MAP", "VENDOR", "ACCOUNTING", "LEGAL_CENTER"];

export function tabHiddenForOrg(user, tabId) {
  const m = orgModules(user);
  if (simpleMenu(user) && SIMPLE_MENU_HIDDEN.includes(tabId)) return true;
  // Fleet: rig services companies, and group admins (docs/features/FLEET.md).
  if (tabId === "FLEET") return !m.rigServices && user?.groupRole !== "admin";
  if (PUMPER_TABS.includes(tabId)) return !m.pumper;
  if (PRODUCTION_TABS.includes(tabId)) return !m.production;
  // Customers are shared by rig services and hydro-testing.
  if (tabId === "CUSTOMERS") return !m.rigServices && !m.hydrotest;
  if (RIG_SERVICE_TABS.includes(tabId)) return !m.rigServices;
  if (HYDRO_TABS.includes(tabId)) return !m.hydrotest;
  if (tabId === "DIVISION_PNL" || tabId === "QUICKBOOKS") return !m.rigServices && !m.hydrotest;
  return false;
}

// Nav group for a tab when the org has BOTH modules (null = keep its
// normal group). Single-module orgs keep today's grouping exactly.
export function moduleGroupFor(user, tabId) {
  if (!hasBothModules(user)) return null;
  if (PUMPER_TABS.includes(tabId) || PRODUCTION_TABS.includes(tabId)) return "PRODUCTION";
  if (RIG_SERVICE_TABS.includes(tabId)) return "RIG_SERVICES";
  return null;
}

// Tank gauging (Well Detail → Tanks) is a pumper tool.
export function showsTankGauging(user) {
  return hasModule(user, "pumper");
}
